import { Document, Page, Text } from '@react-pdf/renderer';
import React from 'react';
import { IQuestionnaire } from '../../modules/unsw-fhir/Questionnaire';
import {
  IQuestionnaireResponse,
  IQuestionnaireResponseItemAnswerComponent,
  IQuestionnaireResponseItemAnswerTableComponent,
  IQuestionnaireResponseItemGroupComponent,
  IQuestionnaireResponseItemTableGroupComponent,
} from '../../modules/unsw-fhir/QuestionnaireResponse';
import {
  isQuestionnaireResponseItemAnswer,
  isQuestionnaireResponseItemGroup,
  isQuestionnaireResponseItemTableGroup,
} from '../../modules/unsw-fhir/QuestionnaireResponse/typeGuards';
import { isQuestionnaireEqualById } from '../../modules/unsw-fhir/Reference';
import { answerItemToString } from '../Report/Questionnaire';
import { styles } from './pdfStyles';

// Display each Questionnaire Item inside PDF
const PDFQuestionnaireItem = (props: {
  item?: IQuestionnaireResponseItemAnswerComponent;
}) => {
  if (!props.item) return null;
  const value = props.item.answer.map(answer => answerItemToString(answer));
  return (
    <Text style={styles.spacing}>
      {/* Question is placed here and do not ask is removed*/}
      <Text style={styles.textBold}>
        {props.item.text ? props.item.text.replace(/DO NOT ASK: /, '') : ''}:
        {'\n'}
      </Text>
      {/* Answer is placed here */}
      {Array.isArray(value) ? (
        value.map((v, index) => (
          <Text style={styles.text} key={index}>
            {v}
            {'\n'}
          </Text>
        ))
      ) : (
        <Text style={styles.text}>
          {value}
          {'\n'}
        </Text>
      )}
    </Text>
  );
};

const PDFQuestionnaireTableItem = (props: {
  item?: IQuestionnaireResponseItemAnswerTableComponent;
}) => {
  if (!props.item) return null;
  const [value] = props.item.answer.map(answer => answerItemToString(answer));
  return (
    <Text style={styles.spacing}>
      {/* Question is placed here and do not ask is removed*/}
      <Text style={styles.text}>
        {'\n'}
        {props.item.text}:{'\n'}
      </Text>
      {/* Answer is placed here */}
      <Text style={styles.text}>{value}</Text>
      {props.item.item?.filter(isQuestionnaireResponseItemAnswer).map(item => {
        const [value] = item.answer.map(answer => answerItemToString(answer));
        return (
          <Text
            key={`${props.item?.linkId}-${item.linkId}`}
            style={styles.text}>
            {' - '}
            {value}
          </Text>
        );
      })}
      <Text style={styles.text}>{'\n'}</Text>
    </Text>
  );
};

const PDFTableSection = (props: {
  item?: IQuestionnaireResponseItemTableGroupComponent;
}) => {
  if (!props.item) return null;
  return (
    <Text style={styles.spacing}>
      {/* Question is placed here and do not ask is removed*/}
      <Text style={styles.textBold}>
        {props.item.text}:{'\n'}
      </Text>
      {/* Answer is placed here */}
      {props.item.item.map((item, i) => (
        <PDFQuestionnaireTableItem key={item.linkId} item={item} />
      ))}
    </Text>
  );
};

// Display each Questionnaire Group inside PDF
const PDFSection = (props: {
  item?: IQuestionnaireResponseItemGroupComponent;
}) => {
  if (!props.item) return null;

  const groups = props.item.item.filter(
    (item): item is IQuestionnaireResponseItemGroupComponent =>
      isQuestionnaireResponseItemGroup(item) &&
      !isQuestionnaireResponseItemTableGroup(item),
  );

  const questionnaireItems = props.item.item.filter(
    item =>
      isQuestionnaireResponseItemAnswer(item) ||
      isQuestionnaireResponseItemTableGroup(item),
  );

  return (
    <>
      <Text style={styles.subtitle}>
        {props.item.linkId}: {props.item.text || ''}
      </Text>
      {questionnaireItems.map(item => {
        if (isQuestionnaireResponseItemTableGroup(item)) {
          return <PDFTableSection key={item.linkId} item={item} />;
        }
        if (isQuestionnaireResponseItemAnswer(item)) {
          return <PDFQuestionnaireItem key={item.linkId} item={item} />;
        }
        return null;
      })}
      {groups.map(group => (
        <PDFSection key={group.linkId} item={group} />
      ))}
    </>
  );
};

// Questionnaire component which displays a PDFViewer for each section (Cristal, PatientValues, etc.)
const PDFQuestionnaire = ({
  questionnaireResponses,
  questionnaires,
  birthDate,
  displayName,
  mrn,
}: {
  questionnaireResponses: {
    data: IQuestionnaireResponse[];
    isLoading: boolean;
    isError: boolean;
  };
  questionnaires: {
    data: IQuestionnaire[];
    isLoading: boolean;
    isError: boolean;
  };
  birthDate: string;
  displayName: string;
  mrn: string;
}) => {
  if (!(questionnaireResponses.isLoading || questionnaires.isLoading)) {
    return (
      <Document>
        <Page size="A4" style={styles.body}>
          {questionnaireResponses.data.map(response => {
            const questionnaire = questionnaires.data.find(q =>
              isQuestionnaireEqualById(response.questionnaire, q.id),
            );
            const title = (questionnaire && questionnaire.title) || '';
            return title ? (
              <>
                <Text style={styles.title}>{title}</Text>
                <Text style={styles.author}>
                  <Text style={styles.authorBold}>Name: </Text>
                  {displayName}
                </Text>
                <Text style={styles.author}>
                  <Text style={styles.authorBold}>Date of Birth: </Text>
                  {birthDate}
                </Text>
                <Text style={styles.author}>
                  <Text style={styles.authorBold}>MRN: </Text>
                  {mrn}
                </Text>
                <Text style={styles.author}>
                  <Text style={styles.authorBold}>Last Updated: </Text>
                  {response.authored ? response.authored.substr(0, 10) : ''}
                </Text>

                {response.item.map(item => {
                  if (!item) return null;
                  return isQuestionnaireResponseItemGroup(item) ? (
                    <PDFSection key={item.id} item={item} />
                  ) : null;
                })}
                <Text
                  style={styles.pageNumber}
                  render={({ pageNumber, totalPages }) =>
                    `${pageNumber} / ${totalPages}`
                  }
                  fixed
                />
              </>
            ) : null;
          })}
        </Page>
      </Document>
    );
  } else {
    return <Document></Document>;
  }
};

export default PDFQuestionnaire;
